import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { getScannerPageHint, isScannerBusy, scannerStatuses, startBarcodeScanner } from '../cameraScanner';

export default function StartPage() {
  const [stats, setStats] = useState(null);
  const [scanner, setScanner] = useState({ status: scannerStatuses.idle, message: '', reason: '' });
  const [unbekannterCode, setUnbekannterCode] = useState(null);
  const [behaelter, setBehaelter] = useState([]);
  const [zielBehaelter, setZielBehaelter] = useState('');
  const scannerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { laden(); }, []);
  useEffect(() => () => scannerRef.current?.stop?.(), []);

  async function laden() {
    try {
      const [b, inv] = await Promise.all([api.get('/behaelter'), api.get('/inventar')]);
      setStats({ behaelter: b.data.length, artikel: inv.data.length });
      setBehaelter(b.data);
      if (b.data[0]) setZielBehaelter(String(b.data[0].id));
    } catch {}
  }

  async function codeScannen() {
    if (isScannerBusy(scanner) || scannerRef.current) return;
    setScanner({ status: scannerStatuses.starting, message: 'Kamera wird gestartet...', reason: '' });

    const controller = await startBarcodeScanner({
      title: 'Code scannen',
      onStateChange: (nextState) => setScanner((current) => ({ ...current, ...nextState })),
      onClose: () => {
        scannerRef.current = null;
        setScanner({ status: scannerStatuses.idle, message: '', reason: '' });
      },
      onError: (error) => {
        if (!error.overlay) scannerRef.current = null;
        setScanner({ status: scannerStatuses.error, message: error.message, reason: error.reason });
      },
      onResult: async (code) => {
        try {
          const r = await api.post('/codes/lookup', { code });
          scannerRef.current = null;
          setScanner({ status: scannerStatuses.idle, message: '', reason: '' });
          if (r.data.type === 'behaelter') navigate(`/behaelter/${r.data.behaelter.id}`);
          else if (r.data.type === 'artikel') navigate(`/behaelter/${r.data.behaelter.id}`);
        } catch (error) {
          scannerRef.current = null;
          const data = error.response?.data;
          if (data?.type === 'unknown_qr') {
            setUnbekannterCode(data.code);
            setScanner({ status: scannerStatuses.idle, message: '', reason: '' });
          } else if (data?.type === 'unknown_barcode') {
            setScanner({ status: scannerStatuses.error, message: 'Dieser Produktbarcode ist noch keinem Artikel zugeordnet.', reason: 'unknown_barcode' });
          } else {
            setScanner({ status: scannerStatuses.error, message: data?.error || 'Code konnte nicht gelesen werden.', reason: 'invalid' });
          }
        }
      },
    });
    scannerRef.current = controller;
  }

  async function codeZuordnen() {
    if (!unbekannterCode || !zielBehaelter) return;
    try {
      const r = await api.post(`/behaelter/${zielBehaelter}/qr/assign`, { code: unbekannterCode });
      setUnbekannterCode(null);
      navigate(`/behaelter/${r.data.id}`);
    } catch (error) {
      setScanner({ status: scannerStatuses.error, message: error.response?.data?.error || 'QR-Code konnte nicht zugeordnet werden.', reason: 'assign' });
    }
  }

  function neuenBehaelterMitCodeAnlegen() {
    if (unbekannterCode) sessionStorage.setItem('pendingQrCode', unbekannterCode);
    setUnbekannterCode(null);
    navigate('/behaelter');
  }

  const scannerHint = getScannerPageHint(scanner);
  const scannerBusy = isScannerBusy(scanner);

  return (
    <div>
      {/* Kompakte Stats */}
      {stats && stats.behaelter > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <Link to="/behaelter" style={{ flex: 1, background: 'white', borderRadius: '10px', padding: '10px 14px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: '3px solid #f59e0b', textDecoration: 'none' }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>{stats.behaelter}</div>
            <div style={{ color: '#64748b', fontSize: '11px' }}>Kisten</div>
          </Link>
          <Link to="/inventar" style={{ flex: 1, background: 'white', borderRadius: '10px', padding: '10px 14px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderTop: '3px solid #6366f1', textDecoration: 'none' }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#6366f1' }}>{stats.artikel}</div>
            <div style={{ color: '#64748b', fontSize: '11px' }}>Artikel</div>
          </Link>
        </div>
      )}

      {/* Onboarding für neue Nutzer */}
      {stats && stats.behaelter === 0 && (
        <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px', color: '#713f12' }}>
          <div style={{ fontWeight: '700', marginBottom: '4px' }}>👋 Willkommen im Lager Organizer!</div>
          <div>Lege zuerst eine <strong>Kiste</strong> an – dann kannst du Artikel darin einlagern.</div>
          <Link to="/behaelter" style={{ display: 'inline-block', marginTop: '8px', padding: '6px 14px', background: '#f59e0b', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '13px' }}>
            Erste Kiste anlegen →
          </Link>
        </div>
      )}

      {/* Scanner-Fehlermeldung */}
      {scanner.message && scanner.status === scannerStatuses.error && (
        <div role="alert" style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', borderRadius: '12px', padding: '12px', marginBottom: '12px', fontSize: '13px', lineHeight: 1.4 }}>
          {scanner.message}
          {scannerHint && <div style={{ marginTop: '8px', color: '#64748b' }}>{scannerHint}</div>}
        </div>
      )}

      {/* Haupt-Aufgaben */}
      <div style={{ marginBottom: '12px', fontWeight: '700', fontSize: '14px', color: '#374151' }}>Was möchtest du tun?</div>

      {/* Primär: Finden */}
      <Link to="/suche"
        style={{ display: 'flex', alignItems: 'center', gap: '14px', background: 'white', borderRadius: '14px', padding: '16px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textDecoration: 'none', marginBottom: '10px', borderLeft: '4px solid #10b981' }}>
        <span style={{ fontSize: '28px' }}>🔍</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e293b' }}>Artikel oder Kiste finden</div>
          <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>Nach Name, Barcode oder Kategorie suchen</div>
        </div>
        <span style={{ fontSize: '20px', color: '#94a3b8' }}>›</span>
      </Link>

      {/* Sekundär: Einlagern */}
      <Link to="/behaelter"
        style={{ display: 'flex', alignItems: 'center', gap: '14px', background: 'white', borderRadius: '14px', padding: '16px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textDecoration: 'none', marginBottom: '10px', borderLeft: '4px solid #f59e0b' }}>
        <span style={{ fontSize: '28px' }}>📦</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '700', fontSize: '15px', color: '#1e293b' }}>Etwas einlagern</div>
          <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>Kiste öffnen und Artikel hinzufügen</div>
        </div>
        <span style={{ fontSize: '20px', color: '#94a3b8' }}>›</span>
      </Link>

      {/* Tertiär: QR-Scanner */}
      <button onClick={codeScannen} disabled={scannerBusy}
        style={{ width: '100%', padding: '14px 18px', background: scannerBusy ? '#fef3c7' : '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: '14px', fontSize: '15px', fontWeight: '600', cursor: scannerBusy ? 'wait' : 'pointer', marginBottom: '16px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '12px', opacity: scannerBusy ? 0.75 : 1 }}>
        <span style={{ fontSize: '24px' }}>{scanner.status === scannerStatuses.starting ? '⏳' : '📷'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '700' }}>{scanner.status === scannerStatuses.starting ? 'Kamera wird gestartet...' : 'QR-Code oder Barcode scannen'}</div>
          <div style={{ fontSize: '12px', color: '#a16207', marginTop: '2px' }}>Kiste direkt öffnen oder Barcode zuordnen</div>
        </div>
        <span style={{ fontSize: '20px', color: '#94a3b8' }}>›</span>
      </button>

      {/* Weitere Optionen */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <Link to="/inventar"
          style={{ background: 'white', borderRadius: '12px', padding: '14px 16px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textDecoration: 'none', borderTop: '2px solid #8b5cf6' }}>
          <div style={{ fontSize: '22px', marginBottom: '4px' }}>📋</div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Inventar</div>
        </Link>
        <Link to="/einstellungen"
          style={{ background: 'white', borderRadius: '12px', padding: '14px 16px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textDecoration: 'none', borderTop: '2px solid #64748b' }}>
          <div style={{ fontSize: '22px', marginBottom: '4px' }}>📍</div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Standorte</div>
        </Link>
      </div>

      {/* QR-Code nicht zugeordnet Dialog */}
      {unbekannterCode && (
        <div role="dialog" aria-label="QR-Code nicht zugeordnet" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', zIndex: 300 }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '22px', width: '100%', maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '18px' }}>QR-Code nicht zugeordnet</h2>
            <p style={{ color: '#64748b', fontSize: '13px', lineHeight: 1.4 }}>Dieser QR-Code ist noch keiner Kiste zugeordnet. Ordne ihn einer vorhandenen Kiste zu oder lege eine neue an.</p>
            <code style={{ display: 'block', background: '#f8fafc', padding: '10px', borderRadius: '8px', fontSize: '12px', overflowWrap: 'anywhere', marginBottom: '12px' }}>{unbekannterCode}</code>
            {behaelter.length > 0 && (
              <select value={zielBehaelter} onChange={e => setZielBehaelter(e.target.value)}
                aria-label="Kiste auswählen"
                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '10px' }}>
                {behaelter.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
              {behaelter.length > 0 && (
                <button onClick={codeZuordnen} disabled={!zielBehaelter} style={{ padding: '12px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700' }}>Vorhandener Kiste zuordnen</button>
              )}
              <button onClick={neuenBehaelterMitCodeAnlegen} style={{ padding: '12px', background: '#eef2ff', color: '#4338ca', border: 'none', borderRadius: '10px', fontWeight: '700' }}>Neue Kiste anlegen</button>
              <button onClick={() => setUnbekannterCode(null)} style={{ padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '10px', fontWeight: '600' }}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
